import { motion, useMotionValue, animate } from "framer-motion";
import { useEffect, useState } from "react";
import { roundCorners } from "svg-round-corners";

const Connector = ({
  id,
  pWidth,
  pHeight,
  startDirection = "h",
  endDirection = "h",
  midPercentage = 50,
  stroke = "#9fa7b3",
  strokeWidth = 3,
  style = {},
  ...rest
}) => {
  const animatedPWidth = useMotionValue(pWidth);
  const [currentPWidth, setCurrentPWidth] = useState(pWidth);

  useEffect(() => {
    const controls = animate(animatedPWidth, pWidth, {
      duration: 0.3,
      ease: "linear",
      onUpdate: (latest) => {
        setCurrentPWidth(latest);
      },
    });
    return () => controls.stop();
  }, [pWidth, animatedPWidth]);

  // Utilizamos currentPWidth (valor animado) en lugar de pWidth para los cálculos
  const absWidth = Math.max(Math.abs(currentPWidth), strokeWidth * 2);
  const absHeight = Math.max(Math.abs(pHeight), strokeWidth * 2);
  const offset = strokeWidth / 2;
  const svgWidth = absWidth + strokeWidth;
  const svgHeight = absHeight + strokeWidth;

  const percentage = Math.max(0, Math.min(midPercentage, 100)) / 100;
  const start = { x: offset, y: offset };
  const end   = { x: absWidth + offset, y: absHeight + offset };

  const isFlatX = pHeight === 0;
  const isFlatY = currentPWidth === 0;

  let path;
  if (isFlatX) {
    // Línea horizontal
    const centerY = (absHeight / 2) + offset;
    path = `M ${start.x} ${centerY} L ${end.x} ${centerY}`;
  } else if (isFlatY) {
    // Línea vertical
    const centerX = (absWidth / 2) + offset;
    path = `M ${centerX} ${start.y} L ${centerX} ${end.y}`;
  } else if (startDirection === "h" && endDirection === "h") {
    const midX = start.x + (end.x - start.x) * percentage;
    path = `M ${start.x} ${start.y}
            L ${midX} ${start.y}
            L ${midX} ${end.y}
            L ${end.x} ${end.y}`;
  } else if (startDirection === "v" && endDirection === "v") {
    const midY = start.y + (end.y - start.y) * percentage;
    path = `M ${start.x} ${start.y}
            L ${start.x} ${midY}
            L ${end.x} ${midY}
            L ${end.x} ${end.y}`;
  } else if (startDirection === "h" && endDirection === "v") {
    path = `M ${start.x} ${start.y}
            L ${end.x} ${start.y}
            L ${end.x} ${end.y}`;
  } else if (startDirection === "v" && endDirection === "h") {
    path = `M ${start.x} ${start.y}
            L ${start.x} ${end.y}
            L ${end.x} ${end.y}`;
  }

  // Redondear esquinas en caso de que no sea una línea totalmente recta
  const thePath = (!isFlatX && !isFlatY)
    ? roundCorners(path, 35, 3).path
    : path;

  const flipX = currentPWidth < 0;
  const flipY = pHeight < 0;

  return (
    <motion.div
      {...rest}
      className="absolute pointer-events-none"
      style={{
        ...style,
        overflow: "visible",
        transform: `scale(${flipX ? "-1" : "1"}, ${flipY ? "-1" : "1"})`
      }}
    >
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      >
        <defs>
          <mask id={`connectorMask_${id}`} maskUnits="userSpaceOnUse">
            <rect width="100%" height="100%" fill="black" />
            <motion.path
              key={`connector_mask_path_${id}`}
              d={thePath}
              stroke="white"
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              // Animación de aparición (de dibujo) del path:
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              exit={{ pathLength: 0, transition: { duration: 0.2 } }}
              transition={{
                duration: 0.7,
                ease: "easeInOut",
                delay: rest.delay,
              }}
            />
          </mask>
        </defs>
        <motion.path
          d={thePath}
          stroke={stroke}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray="6 6"
          strokeLinecap="round"
          mask={`url(#connectorMask_${id})`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.2,
            ease: "easeIn",
            delay: rest.delay,
          }}
        />
      </svg>
    </motion.div>
  );
};

export default Connector;